/**
 * The parity view's model — PD-5.
 *
 * Pure functions, no React, for the reason `matchevents/model.ts` and
 * `conversation/trace.ts` both record: what this package is judged on is the
 * NUMBERS on the screen ("the per-family agreement table matches the offline
 * M10 output"), and a test that can only reach them through a rendered table is
 * testing the table.
 *
 * ## A third reader of one oracle
 *
 * §10.3's cutover gate is computed twice already: live by the matcher
 * (`workers/api/services/extracted-parity.ts`, behind
 * `GET /admin/extracted-parity`) and offline by the replay battery's M10 check
 * in another repository. Both are pinned to the shared golden fixture
 * `test/fixtures/parity-golden/extracted-parity-01.json`, precisely because two
 * implementations of a number that gates a production cutover drift and neither
 * notices.
 *
 * This file adds a THIRD reader, and it is pinned to the same fixture
 * (`parity-model.test.ts` loads it and reproduces `expected.summary` from
 * `expected.rows`). It exists because the admin has two jobs the route's
 * pre-rolled summary cannot do:
 *
 *  1. **A run spans several pebbles.** A scenario run has one pebble per side,
 *     and the route filters by ONE `pebbleId`. A run's parity report is
 *     therefore the merge of N reports — and merging counts means recomputing
 *     ratios, which means the rounding rule has to live here too, identically.
 *  2. **Drill-down groups rows.** The parse-vs-extracted comparison is per
 *     field, grouped by family, ordered so a disagreement is not buried under
 *     forty agreements.
 *
 * Where the definition is repeated it is repeated EXACTLY: ratio = agree /
 * (agree + disagree + extracted_only + parse_only), rounded to 4 dp so the JSON
 * is byte-stable; `null` when nothing was compared, never 0; `ratio_at_threshold`
 * iff compared > 0 and ratio >= threshold, and AT CUTOVER iff that AND
 * compared >= `min_comparisons` (F-PB10-5's evidence floor, 50);
 * `never_applied` fields counted in their own bucket and kept out of `families`
 * and `overall`.
 *
 * AMENDED 2026-09-12 (F-PB10-5). The worker's two rule changes that reach the
 * SCREEN are the evidence floor above and the second never-applied key
 * (`event.eventStory`); the containment rule for `location.storyLocation`
 * changes a verdict at ingest, which this file never recomputes. Neither key
 * list is hardcoded here — `never_applied` arrives in the report and
 * `containment_keys` in `rules` — so this reader keeps working when the worker
 * amends them again.
 */
import type {
  ExtractedParityReport,
  ParityCounts,
  ParityRowRecord,
  ParitySummary,
  ParityVerdict,
} from '../../api/types';

/** The nine cutover units — a key's first dot-segment. Same list, same order as
 *  the worker's `EXTRACTED_FIELD_FAMILIES`; `parity-model.test.ts` asserts it
 *  against the golden fixture rather than trusting this copy. */
export const PARITY_FAMILIES = [
  'additional',
  'circle',
  'event',
  'history',
  'location',
  'shared',
  'them',
  'time',
  'you',
] as const;

const VERDICTS: readonly ParityVerdict[] = ['agree', 'disagree', 'extracted_only', 'parse_only'];

/**
 * The worker's `CUTOVER_MIN_COMPARISONS` (F-PB10-5). Only a FALLBACK: every
 * merge and re-summarise takes the floor from the report it was given, so a
 * worker that raises it does not need an admin deploy to be obeyed. This value
 * is what a report older than F-PB10-5 — which carries no floor at all — is
 * judged by, and judging such a report by 0 would resurrect the exact "flip on
 * twelve comparisons" the floor exists to prevent.
 */
export const PARITY_MIN_COMPARISONS = 50;

export function emptyCounts(): ParityCounts {
  return {
    agree: 0,
    disagree: 0,
    extracted_only: 0,
    parse_only: 0,
    compared: 0,
    ratio: null,
    ratio_at_threshold: false,
    at_cutover: false,
  };
}

/** Step 4 of the definition, to the digit. */
export function parityRatio(c: {
  agree: number;
  disagree: number;
  extracted_only: number;
  parse_only: number;
}): { compared: number; ratio: number | null } {
  const compared = c.agree + c.disagree + c.extracted_only + c.parse_only;
  if (compared === 0) return { compared: 0, ratio: null };
  return { compared, ratio: Math.round((c.agree / compared) * 1e4) / 1e4 };
}

function finalize(c: ParityCounts, threshold: number, minComparisons: number): ParityCounts {
  const { compared, ratio } = parityRatio(c);
  c.compared = compared;
  c.ratio = ratio;
  c.ratio_at_threshold = compared > 0 && ratio !== null && ratio >= threshold;
  c.at_cutover = c.ratio_at_threshold && compared >= minComparisons;
  return c;
}

function add(into: ParityCounts, from: ParityCounts): void {
  for (const v of VERDICTS) into[v] += from[v];
}

export type ParityRowLike = Pick<ParityRowRecord, 'field' | 'field_family' | 'verdict'>;

export interface SummarizeOptions {
  threshold?: number;
  /** The evidence floor (`CUTOVER_MIN_COMPARISONS`). Passed in for the same
   *  reason `neverApplied` is: it is the worker's to change. */
  minComparisons?: number;
  /** The keys that are measured but can never be applied
   *  (`EXTRACTED_APPLY_DENYLIST`). Passed in, never hardcoded: the denylist is
   *  the worker's to change, and the report says what it currently holds. */
  neverApplied?: readonly string[];
  /** Family universe. Every family appears even with no rows — `compared: 0` is
   *  a statement an operator deciding a cutover needs to see. */
  families?: readonly string[];
}

/**
 * Roll rows up per family — the same rollup the worker does, over rows the
 * admin already holds. Used to (a) prove this reader against the golden fixture
 * and (b) re-summarise a filtered subset without another round trip.
 */
export function summarizeRows(
  rows: readonly ParityRowLike[],
  opts: SummarizeOptions = {},
): ParitySummary {
  const threshold = opts.threshold ?? 0.9;
  const minComparisons = opts.minComparisons ?? PARITY_MIN_COMPARISONS;
  const never = new Set(opts.neverApplied ?? []);
  const families: Record<string, ParityCounts> = {};
  for (const f of opts.families ?? PARITY_FAMILIES) families[f] = emptyCounts();
  const overall = emptyCounts();
  const neverApplied = emptyCounts();

  for (const row of rows) {
    const verdict = row.verdict as ParityVerdict;
    if (!VERDICTS.includes(verdict)) continue;
    if (never.has(row.field)) {
      neverApplied[verdict] += 1;
      continue;
    }
    const bucket = families[row.field_family] ?? (families[row.field_family] = emptyCounts());
    bucket[verdict] += 1;
    overall[verdict] += 1;
  }

  for (const f of Object.keys(families)) finalize(families[f], threshold, minComparisons);
  finalize(overall, threshold, minComparisons);
  finalize(neverApplied, threshold, minComparisons);
  neverApplied.at_cutover = false;
  neverApplied.ratio_at_threshold = false;

  return {
    threshold,
    min_comparisons: minComparisons,
    families,
    overall,
    never_applied: {
      fields: [...(opts.neverApplied ?? [])],
      counts: neverApplied,
    },
  };
}

/** Union of two summaries' counts, ratios recomputed (never averaged — an
 *  average of ratios is not the ratio of a union). */
export function mergeSummaries(summaries: readonly ParitySummary[]): ParitySummary {
  const threshold = summaries[0]?.threshold ?? 0.9;
  // The STRICTEST floor any merged report was measured under. A union of two
  // windows is judged by the tighter rule, never loosened by the older one.
  const minComparisons = summaries.reduce(
    (max, s) => Math.max(max, s.min_comparisons ?? PARITY_MIN_COMPARISONS),
    0,
  ) || PARITY_MIN_COMPARISONS;
  const families: Record<string, ParityCounts> = {};
  const overall = emptyCounts();
  const neverApplied = emptyCounts();
  const neverFields = new Set<string>();

  for (const s of summaries) {
    for (const [family, counts] of Object.entries(s.families ?? {})) {
      add((families[family] ??= emptyCounts()), counts);
    }
    add(overall, s.overall ?? emptyCounts());
    if (s.never_applied) {
      add(neverApplied, s.never_applied.counts);
      for (const f of s.never_applied.fields ?? []) neverFields.add(f);
    }
  }

  for (const f of Object.keys(families)) finalize(families[f], threshold, minComparisons);
  finalize(overall, threshold, minComparisons);
  finalize(neverApplied, threshold, minComparisons);
  neverApplied.at_cutover = false;
  neverApplied.ratio_at_threshold = false;

  return {
    threshold,
    min_comparisons: minComparisons,
    families,
    overall,
    never_applied: { fields: [...neverFields], counts: neverApplied },
  };
}

/** The families whose measured agreement permits the `EXTRACTED_FAMILIES` flip. */
export function familiesAtCutover(summary: ParitySummary): string[] {
  return orderedFamilies(summary).filter((f) => summary.families[f]?.at_cutover);
}

/**
 * One report over several pebbles — a scenario RUN, whose sides are separate
 * pebbles the route can only be asked about one at a time.
 *
 * `families_live` and `parity_enabled` describe the DEPLOYMENT, not the window,
 * so they are taken as the union / the AND: a family is live only if it was
 * live for every report merged, and measurement is "on" only if it was on
 * throughout. Anything else would let one stale report claim a flip.
 */
export function mergeReports(reports: readonly ExtractedParityReport[]): ExtractedParityReport {
  const summary = mergeSummaries(reports.map((r) => r.summary));
  const live = reports.length
    ? reports
        .map((r) => new Set(r.families_live ?? []))
        .reduce((acc, s) => new Set([...acc].filter((f) => s.has(f))))
    : new Set<string>();
  const sinces = reports.map((r) => r.window?.since).filter((s): s is number => typeof s === 'number');
  const familyFilters = new Set(reports.map((r) => r.window?.family ?? null));
  return {
    threshold: reports[0]?.threshold ?? summary.threshold,
    min_comparisons: summary.min_comparisons,
    // The rules describe the WORKER, not the window; taking the first is right
    // only because every merged report came from one deployment. A merge that
    // spans an amendment is a merge of two metrics, and `amended` is how a
    // reader would notice.
    rules: reports[0]?.rules,
    parity_enabled: reports.every((r) => r.parity_enabled),
    families_live: [...live],
    families_at_cutover: familiesAtCutover(summary),
    window: {
      since: sinces.length === reports.length && sinces.length > 0 ? Math.min(...sinces) : null,
      family: familyFilters.size === 1 ? [...familyFilters][0] : null,
    },
    summary,
    rows: reports.flatMap((r) => r.rows ?? []).sort((a, b) => b.ts - a.ts),
  };
}

// ---------------------------------------------------------------------------
// What the table draws
// ---------------------------------------------------------------------------

/**
 * A family's state, which is NOT the same question as its ratio:
 *
 *   live       already reading from the extracted path (`EXTRACTED_FAMILIES`).
 *              Its counts are FROZEN at the evidence that justified the flip —
 *              a flipped family stops accumulating rows, so a rising ratio here
 *              would be a gauge welded to a reading.
 *   ready      measured, at or above the gate, ENOUGH of it, not yet flipped.
 *              The one state that is an ACTION: add it to EXTRACTED_FAMILIES.
 *   thin       at or above the ratio but under the evidence floor (F-PB10-5).
 *              NOT `ready` — `circle` sat at 0.83 on twelve comparisons in the
 *              PB-10 gate run, nine of them declined keys agreeing that there
 *              was nothing there, and two more rows would have painted it
 *              flippable. NOT `below` either: nothing is wrong with it, there
 *              is just not enough of it yet. The action is "collect more".
 *   below      measured and under the gate.
 *   unmeasured nothing compared. Distinct from `below` on purpose: "we have no
 *              evidence" and "the evidence says no" are opposite conclusions.
 */
export type CutoverState = 'live' | 'ready' | 'thin' | 'below' | 'unmeasured';

export function cutoverState(counts: ParityCounts | undefined, isLive: boolean): CutoverState {
  if (isLive) return 'live';
  if (!counts || counts.compared === 0) return 'unmeasured';
  if (counts.at_cutover) return 'ready';
  return counts.ratio_at_threshold ? 'thin' : 'below';
}

export interface FamilyRow {
  family: string;
  counts: ParityCounts;
  state: CutoverState;
}

/** Known families first in the worker's order, then anything the report carried
 *  that this copy of the list does not know — a family the admin has never
 *  heard of must still reach the screen. */
export function orderedFamilies(summary: ParitySummary): string[] {
  const known = PARITY_FAMILIES.filter((f) => f in (summary.families ?? {}));
  const extra = Object.keys(summary.families ?? {})
    .filter((f) => !(PARITY_FAMILIES as readonly string[]).includes(f))
    .sort();
  return [...known, ...extra];
}

export function familyRows(report: ExtractedParityReport): FamilyRow[] {
  const live = new Set(report.families_live ?? []);
  return orderedFamilies(report.summary).map((family) => ({
    family,
    counts: report.summary.families[family] ?? emptyCounts(),
    state: cutoverState(report.summary.families[family], live.has(family)),
  }));
}

/** `0.5789` → `57.89%`; `null` → `—` (nothing compared, and 0% would lie). */
export function formatRatio(ratio: number | null | undefined): string {
  if (ratio == null) return '—';
  return `${(ratio * 100).toFixed(2)}%`;
}

// ---------------------------------------------------------------------------
// Drill-down: the parse-vs-extracted comparison, per field
// ---------------------------------------------------------------------------

/** Disagreements first, then the one-sided rows, then the agreements: a screen
 *  that lists forty agreements before the divergence is a screen nobody scrolls. */
const VERDICT_RANK: Record<string, number> = {
  disagree: 0,
  extracted_only: 1,
  parse_only: 2,
  agree: 3,
};

export function compareRows(a: ParityRowLike, b: ParityRowLike): number {
  const rank = (VERDICT_RANK[a.verdict] ?? 9) - (VERDICT_RANK[b.verdict] ?? 9);
  return rank !== 0 ? rank : a.field.localeCompare(b.field);
}

export interface FamilyGroup {
  family: string;
  rows: ParityRowRecord[];
}

/**
 * Group the raw rows by family for the side-by-side. Families in the worker's
 * order; rows worst-first inside each. A row whose family is not in the known
 * list still gets a group — see `orderedFamilies`.
 */
export function groupRowsByFamily(rows: readonly ParityRowRecord[]): FamilyGroup[] {
  const byFamily = new Map<string, ParityRowRecord[]>();
  for (const row of rows) {
    const list = byFamily.get(row.field_family) ?? [];
    list.push(row);
    byFamily.set(row.field_family, list);
  }
  const known = PARITY_FAMILIES.filter((f) => byFamily.has(f));
  const extra = [...byFamily.keys()]
    .filter((f) => !(PARITY_FAMILIES as readonly string[]).includes(f))
    .sort();
  return [...known, ...extra].map((family) => ({
    family,
    rows: [...(byFamily.get(family) ?? [])].sort(compareRows),
  }));
}

/** Rendered member lists are ' | '-joined by the writer; split back for display
 *  so a five-member array is five chips, not one 500-char string. */
export function splitMembers(value: string | null | undefined): string[] {
  if (!value) return [];
  return value
    .split('|')
    .map((s) => s.trim())
    .filter(Boolean);
}
